import { resolveAnalystReading } from '../noyau/analyst-prompt.js';
import { rangementTargetsKept } from '../protocol/rangement.js';
import { segmentsOf } from '../protocol/variables.js';
import type { ProtocolRangement, ProtocolStepDraft } from '../schemas/protocol.js';
import {
  RANGEMENT_DISCARD_REASONS,
  RANGEMENT_OBJECTIVE_STATES,
  type RangementDiscardReason,
} from './output.js';

/**
 * THE INSTRUMENT of the ranger (scenario-succes.md §3, §4; issue #177) - the
 * system block of its prompt, assembled from FOUR parts, in this order:
 *
 * | Part | Written by |
 * |---|---|
 * | the framing (« Tu es le rangeur d'un dispositif d'entretien... ») | the code |
 * | « Les objectifs à ranger »: each target, its identifier, its name and direction | the code, from the protocol's objectives |
 * | « Comment ranger »: the heading, then the CONSIGNE | the heading in code, the consigne by the PROTOCOL played |
 * | the output contract (§3.2, a JSON of extracts, states and formulas) | the code: it is what the program reads |
 *
 * Only the consigne is a setting, exactly as only the reading is for the
 * analyst (`analystSystemParts`): the framing and the contract are what
 * `rangerOutputSchema` reads, and a contract retouched by hand would make every
 * rangement fail. The consigne is versioned with the protocol and frozen with
 * it, so the trace of a retouch is a protocol version, and the FINGERPRINT the
 * server records on every call tells two consignes apart.
 *
 * ## Here, in `shared`, and not in the backend
 *
 * The protocol editor shows « Ce que le rangeur recevra » while the consigne is
 * typed, and the server serves the very same functions: the panel and « l'entrée
 * servie » cannot part company. `rangerSystemParts` is what the panel draws,
 * part by part, and `rangerSystemPrompt` is those parts joined.
 *
 * ## The conversation is NOT in the system block
 *
 * It is the one user message (`rangerConversationMessage`): it changes from one
 * session to the next where the system does not, which is what lets a
 * provider's cache point serve the sessions of one protocol.
 */

/**
 * The version of the ASSEMBLY written in code - what the framing, the targets'
 * layout, the heading of the consigne or the contract say. Bumped when one of
 * them changes; recorded on every call (`rangerCallSchema`).
 */
export const RANGER_PROMPT_VERSION = '1';

/** The heading of the third part - in code: the protocol writes the body of the section, not its title. */
export const RANGER_CONSIGNE_TITLE = '## Comment ranger';

const TARGETS_TITLE = '## Les objectifs à ranger';
const CONTRACT_TITLE = '## Ce que tu rends';

const FRAMING = `Tu es le rangeur d'un dispositif d'entretien qui suit la méthode de Patrick Mathieu. Tu ne parles jamais à la personne : une IA a mené une conversation avec elle, et tu relis cette conversation après coup, en entier, pour ranger ce que la personne a dit sur les objectifs que le protocole te désigne.

La conversation t'est servie en un seul message, tour par tour. Chaque tour porte son numéro, le nom de son étape, la manière dont cette étape s'est terminée (réussie, ratée, ou en cours à la fin de la conversation), le message de l'interviewer (« IA ») et la réponse de la personne (« Personne »). Les tours d'accueil et de simple conversation y sont aussi : la personne a pu dire l'essentiel dès les premiers échanges.

Tu lis les messages de l'interviewer pour savoir à quoi répond une phrase de la personne. Tu ne ranges que les mots de la personne, jamais ceux de l'interviewer.

Tu ranges ce que la consigne te demande de ranger et tu laisses le reste. Ton rangement fait foi : un programme le lit tel quel, et vérifie chacun de tes extraits dans la réponse du tour que tu cites.`;

/**
 * The reasons as the contract explains them. Keyed by the closed list, so a
 * reason added to `RANGEMENT_DISCARD_REASONS` without a sentence does not
 * compile.
 */
const DISCARD_REASON_SENTENCE: Readonly<Record<RangementDiscardReason, string>> = {
  'designe-exemple':
    "le passage ne fait que désigner ou introduire l'exemple (« je vais te raconter la fois où... ») sans porter un objectif",
  negatif: "ce qui ne s'est pas passé, ce qui n'a pas marché, ce qui n'est pas typique",
  generalite: "une généralité sur la personne ou sur le monde, qui ne raconte pas l'exemple",
  'mot-souffle':
    "la personne valide un mot que l'interviewer lui a soufflé, sans rien y ajouter d'elle",
  'autre-episode': 'un autre épisode que celui que tu ranges',
  'decrit-objet':
    "la personne décrit le sujet de l'activité (l'histoire d'un morceau, le sujet d'un livre, les règles d'un jeu) et non ce qu'elle a fait elle-même",
  autre: 'tout autre motif : dis-le dans la consigne, ou range le passage',
};

/**
 * The one clause of the contract the setting « Un passage ne sert qu'un
 * objectif » changes (relecture.md §12, scenario-succes.md §3.2): unticked it is
 * the CR 20 sentence of before #187, to the byte; ticked, the passage serves
 * the objective it carries the most.
 */
function passageSentence(oneObjectivePerPassage: boolean): string {
  return oneObjectivePerPassage
    ? "un passage ne sert qu'un objectif, celui qu'il porte le plus"
    : 'une même phrase peut servir deux objectifs';
}

function contractOf(oneObjectivePerPassage: boolean): string {
  return `Pour chaque objectif à ranger, dans l'ordre où ils sont listés, une fois et une seule :
- « objectiveId » : l'identifiant de l'objectif, tel qu'il est donné plus haut.
- « state » : ${RANGEMENT_OBJECTIVE_STATES.map((state) => `« ${state} »`).join(', ')}. « portée » quand les mots de la personne portent l'objectif ; « imprécise » quand ils l'effleurent sans le porter ; « vide » quand rien ne le porte.
- « missing » : pour une case « imprécise » ou « vide », une phrase qui dit ce qui manque ; une chaîne vide pour une case « portée ».
- « formula » : une phrase qui dit ce que l'objectif contient pour cet exemple, au niveau concret de l'exemple, sans monter d'abstraction. C'est la seule reformulation que tu fais ; une chaîne vide pour une case « vide ».
- « extracts » : les passages de la personne qui portent l'objectif. Chacun a « turn », le numéro du tour qui le porte (le N de [tN · ...]), et « text », recopié MOT POUR MOT de la réponse de la personne à ce tour : sans corriger, sans résumer, sans y mêler les mots de l'interviewer. Un extrait peut être un morceau de phrase, et ${passageSentence(oneObjectivePerPassage)}. Une phrase qui court sur deux tours se cite en deux extraits, chacun dans son tour.

Puis « discarded » : les passages de la personne que tu as lus et que tu n'as rangés nulle part. Chacun a « turn », « text » (mot pour mot, comme un extrait) et « reason », l'une de :
${RANGEMENT_DISCARD_REASONS.map((reason) => `- « ${reason} » : ${DISCARD_REASON_SENTENCE[reason]}.`).join('\n')}
Un passage qui n'est ni rangé ni écarté est simplement non rangé : n'écarte que ce qui mérite d'être dit.

Réponds UNIQUEMENT par un objet JSON, sans texte avant ni après, sans bloc de code, à ce format exact. Les valeurs entre chevrons sont des places à remplir, pas des réponses.

{
  "objectives": [
    {
      "objectiveId": "<identifiant>",
      "state": "<${RANGEMENT_OBJECTIVE_STATES.join(' | ')}>",
      "missing": "<une phrase, ou une chaîne vide>",
      "formula": "<une phrase, ou une chaîne vide>",
      "extracts": [{ "turn": 1, "text": "<mot pour mot>" }]
    }
  ],
  "discarded": [{ "turn": 1, "text": "<mot pour mot>", "reason": "<${RANGEMENT_DISCARD_REASONS.join(' | ')}>" }]
}`;
}

/** One target of the rangement, as the prompt names it. */
export type RangerTarget = {
  readonly id: string;
  readonly name: string;
  readonly direction: string;
};

/** What the system of the ranger is assembled from: the targets and the consigne, both RESOLVED. */
export type RangerPromptInput = {
  readonly targets: readonly RangerTarget[];
  readonly consigne: string;
  /**
   * The setting « Un passage ne sert qu'un objectif » of the section (#187):
   * absent reads unticked, so an input written before it assembles the very same
   * system as before.
   */
  readonly oneObjectivePerPassage?: boolean;
};

/** The four parts of the system, in the order the system assembles them. */
export type RangerSystemPartKind = 'framing' | 'targets' | 'consigne' | 'contract';

/**
 * One part of the system block, as the panel « Ce que le rangeur recevra »
 * draws it: the markdown heading it opens with, and what follows. Only the body
 * of `targets` and of `consigne` come from the protocol.
 */
export type RangerSystemPart = {
  readonly kind: RangerSystemPartKind;
  /** The heading the part opens with - null for the framing, which has none. */
  readonly heading: string | null;
  readonly body: string;
};

/**
 * The text of an objective's direction as the ranger reads it: the objectives it
 * cites by `{{objectif:id}}` by NAME (the same label for a ghost as the
 * consigne gets), and a `{{critere:id}}` dropped - the direction of a criterion
 * is the interviewer's to be served, and a raw identifier in a prompt tells the
 * model nothing.
 */
function directionAsRead(direction: string, steps: readonly ProtocolStepDraft[]): string {
  const resolved = resolveAnalystReading(direction, steps);
  return segmentsOf(resolved)
    .map((segment) => (segment.kind === 'text' ? segment.text : ''))
    .join('')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([.,;:!?])/g, '$1')
    .trim();
}

/**
 * The input of the system for a protocol's section: the targets the steps still
 * carry, in the order of the protocol (`rangementTargetsKept`), each with its
 * trimmed name and its direction; and the consigne with its `{{objectif:id}}`
 * resolved to the NAME of the objective, `ANALYST_READING_ORPHAN_LABEL` for a
 * ghost (the régime of the reading, #158).
 *
 * `steps` are the frozen protocol's for the server and the draft's for the
 * panel: a draft objective whose name is still empty renders empty, which no
 * version can store.
 */
export function rangerPromptInput(
  steps: readonly ProtocolStepDraft[],
  rangement: ProtocolRangement,
): RangerPromptInput {
  const kept = new Set(rangementTargetsKept(steps, rangement).targetObjectiveIds);
  const targets: RangerTarget[] = [];
  for (const step of steps) {
    for (const objective of step.objectives) {
      if (!kept.has(objective.id)) continue;
      targets.push({
        id: objective.id,
        name: objective.name.trim(),
        direction: directionAsRead(objective.direction, steps),
      });
    }
  }
  return {
    targets,
    consigne: resolveAnalystReading(rangement.consigne, steps),
    oneObjectivePerPassage: rangement.oneObjectivePerPassage,
  };
}

function targetsBody(targets: readonly RangerTarget[]): string {
  return targets
    .map((target, rank) => {
      const head = `${rank + 1}. ${target.name} (identifiant : ${target.id})`;
      return target.direction === '' ? head : `${head}\n   Direction : ${target.direction}`;
    })
    .join('\n');
}

/** The four parts for one input, in the order of the system. */
export function rangerSystemParts(input: RangerPromptInput): readonly RangerSystemPart[] {
  return [
    { kind: 'framing', heading: null, body: FRAMING },
    { kind: 'targets', heading: TARGETS_TITLE, body: targetsBody(input.targets) },
    { kind: 'consigne', heading: RANGER_CONSIGNE_TITLE, body: input.consigne },
    {
      kind: 'contract',
      heading: CONTRACT_TITLE,
      body: contractOf(input.oneObjectivePerPassage ?? false),
    },
  ];
}

/** One part as the ranger receives it: its heading, a blank line, then its body. */
export function rangerSystemPartText(part: RangerSystemPart): string {
  return part.heading === null ? part.body : `${part.heading}\n\n${part.body}`;
}

/**
 * THE system block of the ranger for one input: the four parts, separated by
 * one blank line. The same for every session of one protocol version, which is
 * what lets a provider's cache point serve them and what the fingerprint
 * recorded on a call hashes.
 */
export function rangerSystemPrompt(input: RangerPromptInput): string {
  return rangerSystemParts(input).map(rangerSystemPartText).join('\n\n');
}
